// Model pronunciation via the browser's built-in speech synthesis.
//
// Free, instant, no API key, works offline. Voice quality depends on the
// device: Apple and Android ship good English voices; Edge on Windows has
// natural "Online" voices; Chrome on Windows falls back to Google's. If a
// device has no English voice at all, canSpeak() is false and the UI hides
// the play buttons.

import { useCallback, useEffect, useState } from 'react';

const supported = () => typeof window !== 'undefined' && 'speechSynthesis' in window;

// Most natural first. Names differ per platform; anything en-US is the fallback.
const PREFERRED = [
  /Ava|Samantha|Allison|Susan/i, // Apple
  /Online \(Natural\).*United States|Aria|Jenny|Guy/i, // Edge
  /Google US English/i, // Chrome
];

let cachedVoice = null;

const pickVoice = () => {
  if (cachedVoice) return cachedVoice;
  const voices = window.speechSynthesis.getVoices();
  const english = voices.filter((v) => /^en[-_]/i.test(v.lang));
  const us = english.filter((v) => /^en[-_]US/i.test(v.lang));
  for (const re of PREFERRED) {
    const hit = us.find((v) => re.test(v.name)) || english.find((v) => re.test(v.name));
    if (hit) return (cachedVoice = hit);
  }
  cachedVoice = us.find((v) => v.localService) || us[0] || english[0] || null;
  return cachedVoice;
};

// Chrome cuts long utterances off after ~15 s, so speak sentence by sentence.
const chunk = (text) =>
  text
    .match(/[^.!?]+[.!?]*/g)
    ?.map((s) => s.trim())
    .filter(Boolean) || [text];

export function useSpeaker() {
  const [ready, setReady] = useState(() => supported() && window.speechSynthesis.getVoices().length > 0);
  const [speaking, setSpeaking] = useState(null); // the text being spoken, or null

  useEffect(() => {
    if (!supported()) return undefined;
    // Voices load asynchronously on most browsers.
    const onVoices = () => { cachedVoice = null; setReady(window.speechSynthesis.getVoices().length > 0); };
    window.speechSynthesis.addEventListener?.('voiceschanged', onVoices);
    onVoices();
    return () => {
      window.speechSynthesis.removeEventListener?.('voiceschanged', onVoices);
      window.speechSynthesis.cancel();
    };
  }, []);

  const stop = useCallback(() => {
    if (supported()) window.speechSynthesis.cancel();
    setSpeaking(null);
  }, []);

  const speak = useCallback((text, { slow = false } = {}) => {
    if (!supported() || !text) return;
    const synth = window.speechSynthesis;
    synth.cancel();
    const voice = pickVoice();
    const parts = chunk(text);
    setSpeaking(text);
    parts.forEach((part, i) => {
      const u = new SpeechSynthesisUtterance(part);
      u.lang = voice?.lang || 'en-US';
      if (voice) u.voice = voice;
      u.rate = slow ? 0.6 : 0.92;
      if (i === parts.length - 1) {
        u.onend = () => setSpeaking((cur) => (cur === text ? null : cur));
        u.onerror = () => setSpeaking((cur) => (cur === text ? null : cur));
      }
      synth.speak(u);
    });
  }, []);

  return { canSpeak: supported() && ready && !!pickVoice(), speak, stop, speaking };
}
